import { useState } from 'react';
import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/usr-alerts';

export interface AlertSearchCriteria {
    priceMin?: number;
    priceMax?: number;
    areaMin?: number;
    areaMax?: number;
    spaceTypeUuids?: string[];
    spaceStatusUuid?: string;
    featuredType?: string;
    spaceTypeCategory?: string;
    search?: string;
}

export interface AlertItem {
    uuid: string;
    alert_name: string;
    search_criteria: AlertSearchCriteria;
    is_active: boolean;
}

export interface GetAlertsSuccessResponse {
    status: number;
    success: boolean;
    message: string;
    data: AlertItem[];
    links: {
        self: string;
    };
}

/**
 * Requests endpoint to get user alerts.
 */
export const useGetAlerts = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [alerts, setAlerts] = useState<AlertItem[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<number | null>(null);

    const request = async (): Promise<GetAlertsSuccessResponse | undefined> => {
        setLoading(true);
        setSuccess(false);
        setErrorMessage(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            const json = await response.json();

            if (!response.ok) {
                const error = new Error(json.message || json.error || 'Erro ao obter alertas') as ApiError;
                error.statusCode = response.status;
                setErrorMessage(json.message || json.error || 'Erro ao obter alertas');
                throw error;
            }

            const data: GetAlertsSuccessResponse = json;
            setSuccess(true);
            setAlerts(data.data);
            setErrorMessage(null);

            return data;
        } catch (err: any) {
            setSuccess(false);
            handleCommonErrors(err);
        } finally {
            setLoading(false);
            setLastResponseTime(Date.now());
        }
    };

    return {
        request,
        loading,
        success,
        alerts,
        errorMessage,
        lastResponseTime,
    };
};
